import { ArrowDown, Bitcoin, Zap } from 'lucide-react';

import { GithubIcon } from '@/components/icons/GithubIcon';

import { useSiteOwner } from '@/hooks/useSiteOwner';
import { njumpUrl } from '@/lib/owner';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { siteConfig } from '@/config/site';
import { Button } from '@/components/ui/button';

const MARQUEE_TAGS = [
  'bitcoin',
  'nostr',
  'lightning',
  'open-source',
  'value4value',
  'run-your-node',
  'stack-sats',
  'cypherpunk',
  'permissionless',
  'not-your-keys-not-your-coins',
  'zaps',
  'pleb',
];

export function HeroSection() {
  const owner = useSiteOwner();
  const picture = sanitizeUrl(owner.picture);

  return (
    <section className="relative isolate overflow-hidden">
      {/* Backdrop: grid + glowing orbs */}
      <div className="absolute inset-0 -z-10 bg-grid [mask-image:radial-gradient(ellipse_75%_65%_at_50%_40%,black,transparent)]" />
      <div className="absolute -top-24 right-[-8%] -z-10 size-[420px] rounded-full bg-primary/20 blur-[110px] motion-safe:animate-pulse-glow" />
      <div className="absolute bottom-[15%] left-[-10%] -z-10 size-[380px] rounded-full bg-spark/15 blur-[110px] motion-safe:animate-pulse-glow motion-safe:[animation-delay:1.5s]" />

      {/* Floating sats, purely decorative */}
      <Bitcoin
        aria-hidden
        className="absolute left-[8%] top-[22%] -z-10 hidden size-10 text-primary/15 motion-safe:animate-float md:block"
      />
      <Bitcoin
        aria-hidden
        className="absolute right-[10%] top-[55%] -z-10 hidden size-14 text-primary/10 motion-safe:animate-float motion-safe:[animation-delay:2s] md:block"
      />
      <Bitcoin
        aria-hidden
        className="absolute left-[16%] top-[68%] -z-10 hidden size-7 text-spark/20 motion-safe:animate-float motion-safe:[animation-delay:3.5s] md:block"
      />

      <div className="container flex flex-col items-center gap-7 pb-20 pt-20 text-center md:pt-28">
        {/* Avatar */}
        <div className="relative">
          <div className="absolute -inset-2 rounded-full bg-gradient-to-br from-primary via-amber-400 to-spark opacity-60 blur-lg motion-safe:animate-pulse-glow" />
          <div className="relative size-28 overflow-hidden rounded-full border-2 border-primary/50 bg-card md:size-32">
            {picture ? (
              <img src={picture} alt={owner.name} className="size-full object-cover" />
            ) : (
              <div className="flex size-full items-center justify-center bg-gradient-to-br from-primary/25 to-spark/10">
                <Bitcoin className="size-14 text-primary" />
              </div>
            )}
          </div>
          <span
            className="absolute bottom-1.5 right-1.5 size-4 rounded-full border-2 border-background bg-green-500"
            title="Probably shipping"
          />
        </div>

        {/* Intro */}
        <div className="space-y-4">
          <p className="font-mono text-sm text-primary">gm, i'm</p>
          <h1 className="font-display text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
            {owner.name}
          </h1>
          <p className="mx-auto max-w-2xl font-display text-xl font-medium leading-snug text-muted-foreground sm:text-2xl">
            {siteConfig.heroLine1}{' '}
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">
              {siteConfig.heroHighlightA}
            </span>{' '}
            &{' '}
            <span className="bg-gradient-to-r from-spark to-primary bg-clip-text text-transparent">
              {siteConfig.heroHighlightB}
            </span>
            .
          </p>
          <p className="mx-auto max-w-xl leading-relaxed text-muted-foreground">
            {siteConfig.tagline}
          </p>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <Button size="lg" asChild className="font-semibold shadow-lg shadow-primary/20">
            <a href="#notes">
              <ArrowDown className="size-4" />
              See what I'm up to
            </a>
          </Button>
          <Button size="lg" variant="outline" asChild className="font-semibold">
            <a href={njumpUrl(owner.pubkey)} target="_blank" rel="noopener noreferrer">
              <Zap className="size-4" />
              Follow on Nostr
            </a>
          </Button>
          {siteConfig.links.github && (
            <Button size="lg" variant="ghost" asChild>
              <a
                href={siteConfig.links.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub profile"
              >
                <GithubIcon className="size-5" />
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Hashtag marquee */}
      <div className="relative overflow-hidden border-y bg-card/60 py-3.5" aria-hidden>
        <div className="flex w-max motion-safe:animate-marquee">
          {[...MARQUEE_TAGS, ...MARQUEE_TAGS].map((tag, i) => (
            <span key={i} className="mx-5 font-mono text-sm text-muted-foreground">
              <span className="text-primary">#</span>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
